import React from 'react'
import { useMutation } from '@apollo/client'
import { isEmpty } from 'lodash'
import Button from 'components/Button'
import {
  CompanyMemberRole,
  CreateCompany as CreateCompanyType,
  CreateCompanyVariables,
  UserRole,
  UserTypeForFilter,
  useInviteUser,
  useAuth,
} from '@modmd/data'
import { NewUser } from 'sharedComponents/NewUser'
import { Stepper } from 'components/Stepper'
import { useCompanyForm } from 'utils/form/companyForm'
import { GET_COMPANIES as LIST_OF_COMPANIES } from 'internal-portal/routes/Companies/operations'
import { PAGE_LENGTH as COMPANIES_PAGE_LENGTH } from 'internal-portal/routes/Companies/Companies'
import { CreateEditCompany } from 'internal-portal/routes/components/CreateEditCompany'
import { CREATE_COMPANY } from 'internal-portal/routes/components/CreateEditCompany/operations'
import { Box } from 'components/Layout'
import { CheckboxGroup } from 'components/CheckboxGroup'
import { Checkbox } from 'components/Checkbox'
import { RolesLabels } from 'utils/labels'

export interface OnInviteParams {
  userId?: string
  companyId?: string
}

export interface UserInvitationProps {
  onInvite: (params: OnInviteParams) => void
  userType: UserTypeForFilter
}

const UserInvitation = ({ onInvite, userType }: UserInvitationProps) => {
  const [stepIndex, setStepIndex] = React.useState(0)
  const [createCompany, { loading: isCreateCompanyLoading }] = useMutation<
    CreateCompanyType,
    CreateCompanyVariables
  >(CREATE_COMPANY, {
    refetchQueries: [
      {
        query: LIST_OF_COMPANIES,
        variables: {
          pagination: { from: 0, length: COMPANIES_PAGE_LENGTH },
        },
      },
    ],
    awaitRefetchQueries: true,
  })
  const { hasRoles } = useAuth()
  const isSuperAdmin = hasRoles([UserRole.SUPER_ADMIN, UserRole.CLS])
  const numOfSteps = userType === UserTypeForFilter.CompanyOwner ? 2 : 1
  const hasNextStep = stepIndex + 1 < numOfSteps
  const companyForm = useCompanyForm({
    validationSchema: 'fullCompany',
    onSubmit: async () => {
      const { name, Address, Members } = companyForm.values
      const { data } = await createCompany({
        variables: {
          inputData: {
            name,
            Members: [
              ...Members.map(({ User, role }) => ({
                userId: User.id!,
                role,
              })),
            ],
            Address: {
              city: Address?.city || '',
              country: Address?.country || '',
              county: Address?.county || '',
              complement: Address?.complement || '',
              state: Address?.state || '',
              street: Address?.street || '',
              zip: Address?.zip || '',
              lon: Address.lon,
              lat: Address.lat,
            },
          },
        },
      })
      if (data?.createCompany?.id) {
        onInvite({ companyId: data.createCompany.id })
      }
    },
  })

  const { inviteUserData, userInviteForm } = useInviteUser({
    onUserInvite: (user) => {
      if (stepIndex < 1) {
        onInvite({ userId: user.id })
      } else {
        void companyForm.setFieldValue('Members', [{ User: user, role: CompanyMemberRole.Owner }])
        companyForm.handleSubmit()
      }
    },
    validationSchema: 'idOrEmailNoPasswordOnly',
  })

  const handleNextClick = React.useCallback(async () => {
    if (stepIndex === 0) {
      if (!hasNextStep) {
        userInviteForm.handleSubmit()
      } else {
        const inviteError = await userInviteForm.validateForm()
        if (isEmpty(inviteError)) {
          setStepIndex((prevStepIndex) => prevStepIndex + 1)
        } else {
          await userInviteForm.setFieldTouched('email', true)
        }
      }
    }
    if (stepIndex === 1) {
      userInviteForm.handleSubmit()
    }
  }, [stepIndex, userInviteForm, hasNextStep])

  return (
    <Stepper
      width="100%"
      currentStepIndex={stepIndex}
      bottomContent={
        <Box display="inline-grid" gridGap="0.25rem" gridAutoFlow="column">
          {stepIndex > 0 && (
            <Button
              disabled={inviteUserData.loading || isCreateCompanyLoading}
              width="100%"
              onClick={() => setStepIndex((prevStepIndex) => prevStepIndex - 1)}
            >
              Back
            </Button>
          )}
          <Button
            isFetching={inviteUserData.loading || isCreateCompanyLoading}
            width="100%"
            onClick={handleNextClick}
            disabled={inviteUserData.loading || inviteUserData.loading || !userInviteForm.isValid}
          >
            {hasNextStep ? 'Next' : 'Save'}
          </Button>
        </Box>
      }
      appearance="Blank"
    >
      <Stepper.Step
        title="User information"
        render={() => (
          <>
            {isSuperAdmin && (
              <CheckboxGroup
                label="Roles"
                value={userInviteForm.values.roles as string[]}
                onChange={(value) => {
                  void userInviteForm.setFieldValue('roles', value)
                }}
                errorMessage={
                  !userInviteForm?.values?.roles?.length ? 'Select at least one role' : ''
                }
              >
                {Object.values(UserRole).map((role: UserRole) => (
                  <Checkbox
                    key={role}
                    label={RolesLabels[role as keyof typeof RolesLabels]}
                    name={role}
                  />
                ))}
              </CheckboxGroup>
            )}
            <NewUser userForm={userInviteForm} noConfirmationField />
          </>
        )}
      />
      {userType === UserTypeForFilter.CompanyOwner ? (
        <Stepper.Step
          title="Company information"
          render={() => <CreateEditCompany companyForm={companyForm} />}
        />
      ) : undefined}
    </Stepper>
  )
}

export { UserInvitation }
